<template>
  <footer class="site-footer">
    <div class="footer-container">
      <div class="footer-main">
        <!-- Columna 1: Logo, frase, redes sociales -->
        <div class="footer-col footer-col--logo">
          <img :src="logoUrl" alt="Frenkel Logo" class="footer-logo" />
          <p class="footer-phrase">{{ content.phrase }}</p>
          <div class="footer-social" role="navigation" aria-label="redes sociales">
            <a
              v-for="social in socialLinks"
              :key="social.name"
              :href="social.url"
              class="footer-social-link"
              target="_blank"
              rel="noopener"
              :aria-label="social.name"
            >
              <Icon :icon="social.icon" width="22" height="22" />
            </a>
          </div>
        </div>

        <!-- Columna 2: Servicios -->
        <div class="footer-col footer-col--services">
          <span class="footer-title">{{ content.titles.services }}</span>
          <ul>
            <li v-for="service in content.columns.services" :key="service.url">
              <RouterLink :to="service.url">{{ service.label }}</RouterLink>
            </li>
          </ul>
        </div>

        <!-- Columna 3: Enlaces de interés -->
        <div class="footer-col footer-col--links">
          <span class="footer-title">{{ content.titles.links }}</span>
          <ul>
            <li v-for="link in content.columns.links" :key="link.url">
              <RouterLink :to="link.url">{{ link.label }}</RouterLink>
            </li>
          </ul>
        </div>

        <!-- Columna 4: Contacto -->
        <div class="footer-col footer-col--contact">
          <span class="footer-title">{{ content.titles.contact }}</span>
          <div class="footer-contact">
            <div
              v-for="phone in content.columns.contact.phones"
              :key="phone"
              class="footer-contact-item"
            >
              <Icon icon="mdi:phone" width="20" height="20" class="footer-contact-icon" />
              <a :href="`tel:${phone.replace(/ /g, '')}`">{{ phone }}</a>
            </div>
            <div class="footer-contact-item">
              <Icon icon="mdi:email" width="20" height="20" class="footer-contact-icon" />
              <a :href="`mailto:${content.columns.contact.email}`">
                {{ content.columns.contact.email }}
              </a>
            </div>
           
          </div>
        </div>
      </div>

      <!-- Línea inferior -->
      <div class="footer-bottom">
        <span class="footer-copy">{{ content.bottom.copyright }}</span>
        <div class="footer-bottom-links" role="navigation" aria-label="políticas del sitio">
          <RouterLink to="/terminos-condiciones">{{ content.bottom.terms }}</RouterLink>
          <span class="footer-bottom-divider">•</span>
          <RouterLink to="/politicas-cookies">{{ content.bottom.cookies }}</RouterLink>
          <span class="footer-bottom-divider">•</span>
          <RouterLink to="/aviso-privacidad">{{ content.bottom.privacy }}</RouterLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { footerContent } from '@/assets/data/data.ts';
import { Icon } from '@iconify/vue';

export default {
  name: 'SiteFooter',
  props: {
    selectedLang: {
      type: String,
      default: 'es'
    }
  },
  computed: {
    content() {
      return footerContent[this.selectedLang] || footerContent.es;
    },
    socialLinks() {
      return footerContent.social;
    },
    logoUrl() {
      return new URL('@/assets/frenkel-logo.svg', import.meta.url).href;
    }
  },
  components: {
    Icon
  }
};
</script>

<style scoped>
*,
*::before,
*::after { box-sizing: border-box; }

:root {
  --footer-bg: #ffffff;
  --footer-ink: #1e1e1e;
  --footer-ink-soft: #555;
  --footer-accent: #0b72e7;
  --footer-border: #e6e6e6;
  --footer-grid: rgba(0, 0, 0, 0.035);
}

.site-footer {
  background: var(--footer-bg);
  color: var(--footer-ink);
  font-family: 'IBM Plex Mono', monospace;
  letter-spacing: 0.01em;
  position: relative;
  overflow: hidden;
}

/* Fondo geométrico sutil */
.site-footer::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(0deg, var(--footer-grid) 1px, transparent 1px),
    linear-gradient(90deg, var(--footer-grid) 1px, transparent 1px);
  background-size: 28px 28px, 28px 28px;
  pointer-events: none;
}

.footer-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
}

/* MAIN GRID */
.footer-main {
  padding: 44px 0 18px 0;
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr;
  gap: 32px 28px;
}

.footer-col {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.footer-col--logo {
  border: 1px solid var(--footer-border);
  border-radius: 16px;
  padding: 20px;
  background: #fff;
}

/* LOGO */
.footer-logo {
  width: 168px;
  height: auto;
  display: block;
  margin-bottom: 12px;
}

/* TEXTOS */
.footer-phrase {
  margin: 0 0 16px 0;
  font-size: 1rem;
  color: var(--footer-ink-soft);
  line-height: 1.5;
}

.footer-title {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.95rem;
  margin-bottom: 6px;
  position: relative;
  padding-bottom: 8px;
}
.footer-title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 36px;
  background: var(--footer-ink);
  opacity: 0.22;
}

/* LISTAS */
.footer-main ul { list-style: none; padding: 0; margin: 0; }
.footer-main li { margin: 0; }
.footer-main a {
  color: inherit;
  text-decoration: none;
  line-height: 1.7;
  display: inline-block;
  position: relative;
  transition: color 0.2s ease;
}
.footer-main a:hover { color: var(--footer-accent); }

/* Subrayado animado */
.footer-main a::after,
.footer-bottom a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0%;
  height: 2px;
  background-color: currentColor;
  transition: width 0.3s ease;
}
.footer-main a:hover::after,
.footer-bottom a:hover::after { width: 100%; }

/* CONTACTO */
.footer-contact { display: flex; flex-direction: column; gap: 6px; }
.footer-contact-item { display: flex; align-items: center; gap: 8px; }
.footer-contact-icon { color: var(--footer-accent); flex-shrink: 0; }

/* SOCIAL */
.footer-social { display: flex; gap: 10px; flex-wrap: wrap; }
.footer-social-link {
  border: 1px solid var(--footer-border);
  border-radius: 12px;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  background: #fff;
}
.footer-social-link:hover {
  transform: translateY(-2px);
  border-color: var(--footer-ink);
  color: var(--footer-accent);
}

/* BOTTOM */
.footer-bottom {
  padding: 16px 0 28px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px dashed var(--footer-border);
  color: var(--footer-ink-soft);
  font-size: 0.95rem;
}
.footer-bottom-links { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.footer-bottom-divider { color: #9aa0a6; opacity: 0.8; }
.footer-copy { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ===========================
   BREAKPOINTS
   =========================== */

/* md (≤ 992px): 2 columnas; SIN expandir el logo a todo el ancho */
@media (max-width: 992px) {
  .footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 26px 22px;
  }
  .footer-col--logo {
    grid-column: auto; /* ya no ocupa todo el ancho */
  }
}

/* sm (≤ 576.98px): 1 columna y OCULTAR Servicios + Enlaces */
@media (max-width: 576.98px) {
  .footer-container { padding: 0 16px; }
  .footer-main {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 36px 0 10px 0;
  }

  /* ocultar columnas pedidas */
  .footer-col--services,
  .footer-col--links { display: none; }

  .footer-col--logo { padding: 18px; border-radius: 14px; }
  .footer-logo { width: 156px; }
  .footer-phrase { font-size: 1.02rem; }
  .footer-title { font-size: 1rem; }
  .footer-main a { line-height: 1.9; font-size: 0.98rem; }

  .footer-bottom {
    padding: 14px 0 24px 0;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  .footer-bottom-links { gap: 8px; }
}
</style>
